// The live usage monitor (module 10 U70, U71; B554). Mike, 2026-09-20: "I am buying more model
// usage ... I would like to see where it is all going and what is using what, like the classifier
// for example", and "only accessable by me".
//
// The super admin's page, in U28's Match centre grammar. It answers four questions in this order,
// because that is the order they are asked in: what am I spending now, what is using it, which
// model, and what will stop me. Then the tokens, then, in plain words, what the page does NOT
// count -- because a monitor that quietly leaves out Twilio, Recall, Snowflake and Mike's own
// Claude Code sessions would read as the whole bill.
//
// U71 sets aside U32 here: model names, lane names and prompt files are on the screen on purpose.
// They are the subject of the page, not build detail leaking onto a user's surface.
import { ActionInput, ActionButton, Chip } from '../parts/index.js';
import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { ApiError, Unauthenticated } from '../api.js';
import { clock, dayLabel, exact, plural, since, tokens, usd } from './format.js';
import { leadPlates, windowWord } from './lead.js';
import './usage.css';
import { usage, type CeilingView, type CostSource, type CostView, type DayView, type HourView, type LaneView, type ReceiptTotals, type SpendRow, type UsageView, type UsageWindow } from './api.js';

/** How often the page re-reads while it is in front. The rollup costs one bounded scan. */
const REFRESH_MS = 20_000;

const WINDOWS: ReadonlyArray<{ id: UsageWindow; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: 'custom', label: 'A range' },
];

/** A calendar day in UTC, which is the day every other figure on this page is counted in. */
const dayOf = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

export function Usage(): ReactElement {
  const [window_, setWindow] = useState<UsageWindow>('today');
  const [fromDay, setFromDay] = useState(() => dayOf(Date.now() - 13 * 86_400_000));
  const [toDay, setToDay] = useState(() => dayOf(Date.now()));
  const [view, setView] = useState<UsageView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const seq = useRef(0);

  const load = useCallback(async (w: UsageWindow, f: string, t: string) => {
    const mine = ++seq.current;
    try {
      const v = await usage.read(w, f, t);
      if (mine === seq.current) { setView(v); setError(null); setNow(Date.now()); }
    } catch (err) {
      if (mine !== seq.current) return;
      // Never swallowed: a page about money that goes quiet when it cannot read is worse than
      // one that says so (AUDIT-2026-09-19, "swallowed errors").
      if (err instanceof Unauthenticated) { window.location.href = '/'; return; }
      setError(err instanceof ApiError ? err.message : 'The usage figures could not be read.');
    }
  }, []);

  useEffect(() => { void load(window_, fromDay, toDay); }, [load, window_, fromDay, toDay]);

  useEffect(() => {
    const tick = () => { if (!document.hidden) void load(window_, fromDay, toDay); };
    const timer = setInterval(tick, REFRESH_MS);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', tick); };
  }, [load, window_, fromDay, toDay]);

  if (error !== null) {
    return (
      <div className="up">
        <h1 className="up-title">Usage</h1>
        <p className="up-said">{error}</p>
      </div>
    );
  }
  if (view === null) return <div className="up"><h1 className="up-title">Usage</h1><p className="up-said">Reading the model log…</p></div>;

  const resetsAt = clock(view.dayEnd);

  return (
    <div className="up">
      <header className="up-top">
        <h1 className="up-title">Usage</h1>
        <span className="up-live" aria-label={`Figures as at ${clock(view.asAt)}`}>
          <i className="up-dot" aria-hidden="true" />{clock(view.asAt)}
        </span>
        <div className="up-win" role="group" aria-label="How far back">
          {WINDOWS.map((w) => (
            <ActionButton reading key={w.id} type="button" aria-pressed={w.id === window_}
              className={w.id === window_ ? 'on' : undefined} onClick={() => setWindow(w.id)}>{w.label}</ActionButton>
          ))}
        </div>
        {window_ === 'custom' && (
          <div className="up-range">
            <label>From <ActionInput reading type="date" value={fromDay} max={toDay}
              onChange={(e) => setFromDay(e.target.value)} /></label>
            <label>to <ActionInput reading type="date" value={toDay} min={fromDay} max={dayOf(Date.now())}
              onChange={(e) => setToDay(e.target.value)} /></label>
            <span className="up-note">Both days counted, in UTC, as every other figure here is.</span>
          </div>
        )}
      </header>

      {!view.ready && (
        <p className="up-warnline">
          Nothing has been rolled up yet, so these figures are empty rather than nil. The model log is there; the
          summary it is read from has not been built.
        </p>
      )}

      <Bill cost={view.cost} window={view.window} />
      {view.cost.paid !== null && view.cost.paid.count > 0 && <Paid paid={view.cost.paid} window={view.window} cobrainUsd={view.totals.usd} />}

      <Section title="The model bill, in detail" note="Everything below takes the Cobrain line above apart. It is Cobrain's own model calls only — your own Claude Code and claude.ai have no meter behind them, only the charge.">
        <Lead view={view} resetsAt={resetsAt} />
      </Section>

      <Section title="The last two days, hour by hour" note={view.window === 'today' ? 'Every hour, including the ones that cost nothing.' : 'Every hour, including the ones that cost nothing. This one is always the last two days — the whole window is in "By day" below.'}>
        <Hours hours={view.hours} />
      </Section>

      <Section
        title="What is using it"
        note={`Every penny in this window, biggest first: what Cobrain spent, itemised, beside what was charged to you with no meter behind it. ${view.spend.length} line${view.spend.length === 1 ? '' : 's'}. Open one that Cobrain measured to see the prompt behind it.`}
      >
        {view.spend.length === 0
          ? <p className="up-none">Nothing was spent in this window.</p>
          : <ul className="up-lanes">{view.spend.map((r) => <Spend key={r.id} row={r} />)}</ul>}
        {view.cost.creditBoughtUsd > 0 && (
          <p className="up-note">
            You also bought {usd(view.cost.creditBoughtUsd)} of API credit in this window. It is not a line above,
            because it is how the measured lines are paid for — counting it as well would count those calls twice.
          </p>
        )}
        {view.totals.wastedUsd > 0 && view.totals.usd > 0 && (
          <p className="up-warnline">
            {usd(view.totals.wastedUsd)} of Cobrain's own {usd(view.totals.usd)} — {Math.round((view.totals.wastedUsd / view.totals.usd) * 100)}% —
            {' '}was spent on calls that failed: a refusal, or an answer Cobrain would not accept against its own schema. The
            {' '}tokens were read and billed either way.
          </p>
        )}
      </Section>

      <Section title="Which model" note="Cobrain's own brains are in the list. They do the work and cost nothing, which is the point of them.">
        <div className="up-scroll">
        <table className="up-table">
          <thead><tr><th scope="col">Model</th><th scope="col">Calls</th><th scope="col">In</th><th scope="col">Cached</th><th scope="col">Out</th><th scope="col">Cost</th></tr></thead>
          <tbody>
            {view.models.map((m) => (
              <tr key={m.model}>
                <th scope="row">
                  {m.label}
                  {m.model.startsWith(m.label) && m.model !== m.label && <span className="up-ver" title={m.model}>{m.model.slice(m.label.length)}</span>}
                  {m.kind === 'cobrain' && <Chip className="up-tag">Cobrain's own</Chip>}
                  {m.kind === 'unpriced' && <Chip className="up-tag up-tag-warn">No price</Chip>}
                </th>
                <td>{exact(m.calls)}</td>
                <td title={exact(m.tokens.input)}>{tokens(m.tokens.input)}</td>
                <td title={`${exact(m.tokens.cacheRead)} read, ${exact(m.tokens.cacheWrite)} written`}>{tokens(m.tokens.cacheRead)}</td>
                <td title={exact(m.tokens.output)}>{tokens(m.tokens.output)}</td>
                <td className="up-num">{m.kind === 'unpriced' ? '—' : usd(m.usd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {view.totals.unpricedModels.length > 0 && (
          <p className="up-warnline">
            {plural(view.totals.unpricedCalls, 'call')} ran on a model this page has no
            price for ({view.totals.unpricedModels.join(', ')}). Their tokens are counted above; their cost is not in any total
            on this page, and it is not nothing.
          </p>
        )}
      </Section>

      {view.days.length > 1 && (
        <Section title="By day" note="Each bar is a day, split by lane in the order of the list above.">
          <Days days={view.days} lanes={view.lanes} />
        </Section>
      )}

      <Section title="Tokens" note="What was read fresh, what was written to the cache, what came back from it, and what was written out.">
        <div className="up-tok">
          <Fig label="Read fresh" v={view.totals.tokens.input} />
          <Fig label="Written to cache" v={view.totals.tokens.cacheWrite} />
          <Fig label="Read from cache" v={view.totals.tokens.cacheRead} />
          <Fig label="Written out" v={view.totals.tokens.output} />
        </div>
        <p className="up-note">
          The cache saved <b>{usd(view.cache.savedUsd)}</b> in this window: those {tokens(view.cache.readTokens)} tokens
          billed at a tenth of what reading them fresh would have cost.
        </p>
      </Section>

      <Section title="What will stop, and when" note={`Each lane's own daily ceiling. The day resets at ${resetsAt}.`}>
        <ul className="up-ceils">{view.ceilings.map((c) => <Ceiling key={c.purpose} c={c} />)}</ul>
      </Section>

      <Section
        title={view.notCounted.length === 0 ? 'What this does not count' : `What is still missing from the total (${view.notCounted.length})`}
        note="Real money that nothing here can see yet. Everything else on this page is in the figure at the top."
      >
        {view.notCounted.length === 0
          ? <p className="up-none">Nothing. Every source Cobrain uses is priced in the total above.</p>
          : <ul className="up-scope">{view.notCounted.map((line) => <li key={line}>{line}</li>)}</ul>}
        <p className="up-note">
          The model figures are Cobrain's own calls in the {view.store.name} store, priced at the published list prices
          and summed from the log written before every answer was used. The Anthropic charges are the vendor's own
          receipts, read from your mail. Last brought up to date {since(view.rolledAt, now)}.
        </p>
      </Section>
    </div>
  );
}


/** What Cobrain costs to run, every source of it, with the total first (U79). */
function Bill({ cost, window: w }: { cost: CostView; window: UsageWindow }): ReactElement {
  const counted = cost.sources.filter((s) => s.usd !== null).length;
  return (
    <section className="up-sec">
      <div className="up-bill">
        <div className="up-bill-lead">
          <b>{usd(cost.usd)}</b>
          <span>spent {windowWord(w)} — Cobrain and your own Claude together</span>
          <small>
            {cost.taxUsd > 0 && `${usd(cost.netUsd)} of it before tax. `}
            {counted} of {cost.sources.length} sources priced.{' '}
            {cost.missing > 0
              ? `${plural(cost.missing, 'source is', 'sources are')} not metered yet, so this is a floor, not the whole bill.`
              : 'Every source is priced.'}
            {cost.creditBoughtUsd > 0 && (
              ` The ${usd(cost.creditBoughtUsd)} of API credit you bought is not in it: that buys the Cobrain calls, which are counted already.`
            )}
          </small>
        </div>
        <ul className="up-srcs">{cost.sources.map((s) => <Source key={s.id} s={s} />)}</ul>
      </div>
      {cost.accountError !== null && (
        <p className="up-warnline">The Anthropic cost report could not be read, so the Cobrain line is Cobrain's own measurement: {cost.accountError}</p>
      )}
      {cost.account !== null && cost.account.lines.length > 0 && (
        <details className="up-acct">
          <summary>What the Anthropic account was billed for</summary>
          <div className="up-scroll">
            <table className="up-table up-table-sm">
              <thead><tr><th scope="col">Line</th><th scope="col">Cost</th></tr></thead>
              <tbody>{cost.account.lines.map((l) => (
                <tr key={l.description}><th scope="row">{l.description}</th><td className="up-num">{usd(l.usd)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </details>
      )}
    </section>
  );
}

/**
 * Every Anthropic charge as the vendor billed it (U84). It is no longer a rival total -- the
 * charges are inside the figure above -- so this block exists to show the receipts themselves:
 * what was bought, when, and how much of it was tax.
 */
function Paid({ paid, window: w, cobrainUsd }: { paid: ReceiptTotals; window: UsageWindow; cobrainUsd: number }): ReactElement {
  const api = paid.byKind.find((k) => k.kind === 'api_credit');
  return (
    <Section
      title="Every Anthropic charge, as they billed it"
      note={`${plural(paid.count, 'receipt')} in your mail ${windowWord(w)}. The extra-usage and subscription charges are in the total above; a credit purchase is not, because it buys the calls that total already counts.`}
    >
      <div className="up-bill">
        <div className="up-bill-lead">
          <b>{usd(paid.paidUsd)}</b>
          <span>charged by Anthropic {windowWord(w)}</span>
          <small>
            VAT included{Number.isFinite(paid.netUsd) && paid.netUsd > 0 ? ` — ${usd(paid.netUsd)} of it before tax` : ''}.
            {paid.unread > 0 && ` ${plural(paid.unread, 'receipt')} could not be read and ${paid.unread === 1 ? 'is' : 'are'} not in this figure.`}
          </small>
        </div>
        <ul className="up-srcs">
          {paid.byKind.map((k) => (
            <li key={k.kind} className="up-src">
              <span className="up-src-head">
                <b>{k.name}</b>
                <Chip className="up-tag">{plural(k.count, 'charge')}</Chip>
                <span className="up-src-cost">{usd(k.paidUsd)}</span>
              </span>
            </li>
          ))}
        </ul>
        {api !== undefined && (
          <p className="up-note">
            The credit is what Cobrain draws on: {Number.isFinite(api.netUsd) && api.netUsd > 0
              ? `${usd(api.netUsd)} bought before tax (${usd(api.paidUsd)} charged)`
              : `${usd(api.paidUsd)} charged`}, against {usd(cobrainUsd)} of measured use over the same stretch. Credit is bought in
            lumps and spent over the days after, so the two only meet over a week or more.
          </p>
        )}
      </div>
    </Section>
  );
}

const STATE_WORD: Record<CostSource['state'], string> = {
  measured: 'measured',
  billed: 'the vendor\'s own bill',
  not_connected: 'not in use',
  needs_credential: 'needs a key',
  not_metered: 'not metered',
};

function Source({ s }: { s: CostSource }): ReactElement {
  const unknown = s.usd === null;
  // A charged figure and a list-price figure are not the same kind of number, so a line that
  // carries tax says so rather than leaving it to be found by subtraction.
  const taxed = s.usd !== null && s.netUsd !== null && s.netUsd > 0 && s.netUsd !== s.usd;
  return (
    <li className={unknown ? 'up-src unknown' : 'up-src'}>
      <span className="up-src-head">
        <b>{s.name}</b>
        <Chip className="up-tag">{STATE_WORD[s.state]}</Chip>
        {taxed && <Chip className="up-tag">VAT included</Chip>}
        <span className="up-src-cost" title={taxed ? `${usd(s.netUsd!)} before tax` : undefined}>{unknown ? '—' : usd(s.usd!)}</span>
      </span>
      <span className="up-src-what">{s.what}</span>
      <span className="up-note">{s.basis}</span>
      {s.todo !== null && <span className="up-src-todo">{s.todo}</span>}
    </li>
  );
}

function Section({ title, note, children }: { title: string; note?: string; children: ReactNode }): ReactElement {
  return (
    <section className="up-sec">
      <div className="up-sech"><h2>{title}</h2>{note !== undefined ? <p className="up-note">{note}</p> : null}</div>
      {children}
    </section>
  );
}

function Fig({ label, v }: { label: string; v: number }): ReactElement {
  return <div className="up-fig"><b title={exact(v)}>{tokens(v)}</b><span>{label}</span></div>;
}

function Lead({ view, resetsAt }: { view: UsageView; resetsAt: string }): ReactElement {
  // Which figures these are, and which window they are about, is decided in lead.ts and tested
  // there (B675). The component only draws them.
  return (
    <div className="up-lead">
      {leadPlates(view, resetsAt, { usd, exact, plural, clock }).map((p) => (
        <div key={p.label} className={p.warn === true ? 'up-plate up-plate-warn' : 'up-plate'}>
          <b>{typeof p.value === 'number' ? usd(p.value) : p.value}</b>
          <span>{p.label}</span>
          <small>{p.note}</small>
        </div>
      ))}
    </div>
  );
}

/**
 * One line of the whole bill (U108). A line Cobrain measured opens onto its prompts and models; a
 * line that was merely charged to us says so, and says where the breakdown lives instead of
 * leaving a gap the reader has to explain to themselves.
 */
function Spend({ row }: { row: SpendRow }): ReactElement {
  const [open, setOpen] = useState(false);
  if (row.lane !== null) return <Lane lane={row.lane} share={row.sharePct} />;
  return (
    <li className={open ? 'up-lane open' : 'up-lane'}>
      <ActionButton reading type="button" className="up-lane-btn" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className="up-lane-head">
          <b className="up-lane-name">{row.name}</b>
          <Chip className="up-tag">{row.how === 'charged' ? 'charged to you' : 'measured'}</Chip>
          <span className="up-lane-cost">{usd(row.usd)}</span>
        </span>
        <span className="up-bar" aria-hidden="true"><i style={{ width: `${Math.max(row.sharePct, 0.6)}%` }} /></span>
        <span className="up-lane-meta">
          {row.sharePct}% of this window
          {row.noMeter !== null && <> · <b className="up-bad">no meter behind it</b></>}
        </span>
        <span className="up-lane-does">{row.what}</span>
      </ActionButton>
      {open && row.noMeter !== null && (
        <div className="up-lane-open"><p className="up-note">{row.noMeter}</p></div>
      )}
    </li>
  );
}

function Lane({ lane, share }: { lane: LaneView; share?: number }): ReactElement {
  const [open, setOpen] = useState(false);
  // `lane.sharePct` is the lane's share of COBRAIN's model bill. The section around it now shows
  // the WHOLE bill, so when a share of that is passed in it is the one to print: otherwise the
  // classifier reads as 67% of a window it is 15% of (U108).
  const pct = share ?? lane.sharePct;
  return (
    <li className={open ? 'up-lane open' : 'up-lane'}>
      <ActionButton reading type="button" className="up-lane-btn" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className="up-lane-head">
          <b className="up-lane-name">{lane.name}</b>
          <span className="up-lane-cost">{lane.priced ? usd(lane.usd) : '—'}</span>
        </span>
        <span className="up-bar" aria-hidden="true"><i style={{ width: `${Math.max(pct, 0.6)}%` }} /></span>
        <span className="up-lane-meta">
          {lane.priced
            ? <>{pct}% · {plural(lane.calls, 'call')} · {usd(lane.perCallUsd)} each</>
            : <>{plural(lane.calls, 'call')} · <b className="up-bad">no price for the model that made them</b></>}
          {lane.failed > 0 && <> · <b className="up-bad">{exact(lane.failed)} failed{lane.wastedUsd > 0 && `, ${usd(lane.wastedUsd)} of it wasted`}</b></>}
          {lane.priced && lane.unpricedCalls > 0 && <> · <b className="up-bad">{exact(lane.unpricedCalls)} unpriced</b></>}
        </span>
        <span className="up-lane-does">{lane.does}</span>
      </ActionButton>
      {open && (
        <div className="up-lane-open">
          <div className="up-scroll">
          <table className="up-table up-table-sm">
            <thead><tr><th scope="col">Prompt</th><th scope="col">Calls</th><th scope="col">Cost</th></tr></thead>
            <tbody>{lane.prompts.map((p) => (
              <tr key={p.prompt}><th scope="row">{p.prompt}</th><td>{exact(p.calls)}</td><td className="up-num">{usd(p.usd)}</td></tr>
            ))}</tbody>
          </table>
          </div>
          <div className="up-scroll">
          <table className="up-table up-table-sm">
            <thead><tr><th scope="col">Model</th><th scope="col">Calls</th><th scope="col">Cost</th></tr></thead>
            <tbody>{lane.models.map((m) => (
              <tr key={m.model}>
                <th scope="row">{m.label}{m.model.startsWith(m.label) && m.model !== m.label && <span className="up-ver" title={m.model}>{m.model.slice(m.label.length)}</span>}</th>
                <td>{exact(m.calls)}</td><td className="up-num">{m.priced ? usd(m.usd) : '—'}</td>
              </tr>
            ))}</tbody>
          </table>
          </div>
          <p className="up-note">
            {tokens(lane.tokens.input)} read fresh, {tokens(lane.tokens.cacheRead)} from the cache,
            {' '}{tokens(lane.tokens.output)} written out.
          </p>
        </div>
      )}
    </li>
  );
}

function Hours({ hours }: { hours: HourView[] }): ReactElement {
  const top = Math.max(...hours.map((h) => h.usd), 0.01);
  return (
    <div className="up-hours">
      {hours.map((h) => (
        <div key={h.hour} className="up-hbar" title={`${clock(h.hour)} — ${usd(h.usd)}, ${exact(h.calls)} calls`}>
          <i style={{ height: `${Math.round((h.usd / top) * 100)}%` }} />
        </div>
      ))}
      <div className="up-haxis"><span>{hours.length > 0 ? clock(hours[0]!.hour) : ''}</span><span>{usd(top)} at the tallest</span><span>now</span></div>
    </div>
  );
}

function Days({ days, lanes }: { days: DayView[]; lanes: LaneView[] }): ReactElement {
  const top = Math.max(...days.map((d) => d.usd), 0.01);
  const order = lanes.map((l) => l.purpose);
  return (
    <div className="up-days">
      {days.map((d) => (
        <div key={d.day} className="up-dbar" title={`${dayLabel(d.day)} — ${usd(d.usd)}, ${exact(d.calls)} calls`}>
          <div className="up-dstack" style={{ height: `${Math.round((d.usd / top) * 100)}%` }}>
            {[...d.lanes].sort((a, b) => order.indexOf(a.purpose) - order.indexOf(b.purpose)).map((l, i) => (
              <i key={l.purpose} className={`up-l${Math.min(i, 5)}`} style={{ flexGrow: Math.max(l.usd, 0.0001) }} />
            ))}
          </div>
          <span className="up-dlabel">{dayLabel(d.day)}</span>
        </div>
      ))}
    </div>
  );
}

function Ceiling({ c }: { c: CeilingView }): ReactElement {
  const share = c.ceilingUsd > 0 ? Math.min(100, (c.spentTodayUsd / c.ceilingUsd) * 100) : 0;
  return (
    <li className={c.reached ? 'up-ceil reached' : 'up-ceil'}>
      <span className="up-ceil-head">
        <b>{c.name}</b>
        <span>{usd(c.spentTodayUsd)} of {usd(c.ceilingUsd)}{c.reached && <b className="up-bad"> — stopped</b>}</span>
      </span>
      <span className="up-bar" aria-hidden="true"><i className={c.reached ? 'bad' : undefined} style={{ width: `${Math.max(share, 0.6)}%` }} /></span>
      <span className="up-note">{c.note}</span>
    </li>
  );
}
